import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import AuthSubmitButton from 'src/features/auth/components/auth-submit-button'
import { Form } from 'src/shared/components/ui/forms/form'
import AuthInputFormField from 'src/shared/features/auth/components/auth-input-form-field'
import type { FirstNameFormData } from 'src/shared/features/users/schemas/user'
import { firstNameFormSchema } from 'src/shared/features/users/schemas/user'

type FirstNameStepProps = {
  onNext: (data: FirstNameFormData) => void
  initialData?: Partial<FirstNameFormData>
}

const FirstNameStep = ({ onNext, initialData }: FirstNameStepProps) => {
  const form = useForm<FirstNameFormData>({
    resolver: zodResolver(firstNameFormSchema),
    defaultValues: {
      firstName: initialData?.firstName?.trim() || '',
    },
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  // Watch for firstname changes and strip whitespace
  const firstName = form.watch('firstName')

  useEffect(() => {
    if (firstName && firstName.trim() !== firstName) {
      form.setValue('firstName', firstName.trim(), { shouldValidate: true })
    }
  }, [firstName, form])

  const onSubmit = async (data: FirstNameFormData) => {
    if (isSubmitting) return

    setIsSubmitting(true)
    try {
      // Trim whitespace from inputs before submission
      const formattedData = {
        firstName: data.firstName.trim(),
      }

      onNext(formattedData)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <AuthInputFormField control={form.control} name="firstName" label="First Name" />
        <AuthSubmitButton type="submit" isLoading={isSubmitting} className="mt-[25px] bg-primary">
          Continue
        </AuthSubmitButton>
      </form>
    </Form>
  )
}

FirstNameStep.displayName = 'FirstNameStep'

export default FirstNameStep
